import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { missingLinkError } from '@execonline-inc/links.private';
import { toTask } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import CreateShareUrlStore from '.';
import { callApi } from '../../../../../../Appy';
import { findLinkBy, findLinkByT } from '../../../../../../Links';
import ReactionComponent, { RCProps } from '../../../../../../ReactionComponent';
import { Link } from '../../../../../../Resource/Types';
import { learningCollectionResourceDecoder } from '../../../../Decoders';
import { LearningCollectionResource } from '../../../../Types';
import ShareableLinksStore from '../Store';
import { ShareableLinksResource } from '../Types';
import { ShareUrlError, State } from './Types';

interface Props extends RCProps<CreateShareUrlStore> {
  store: CreateShareUrlStore;
  pageStore: ShareableLinksStore;
  learningCollectionResource: ShareableLinksResource;
}

interface CreateShareUrlOut {
  id: number;
}

const createShareUrlEncoder = (id: number): CreateShareUrlOut => ({
  id,
});

const handleReportsError = (store: CreateShareUrlStore) => (error: ShareUrlError) => {
  store.error(error);
  warnAndNotify('CreateShareUrlReactions', error.kind, error);
};

// The create and generate endpoints answer with the full collection resource.
// The page reads its own lightweight payload again so the list and the
// "Showing N out of N" count include the new links (DVLP-8041).
const linksAdded =
  (store: CreateShareUrlStore, pageStore: ShareableLinksStore, current: ShareableLinksResource) =>
  () => {
    store.ready(current);
    pageStore.reload();
  };

// A collection with no courses generates nothing; reloading would ask the
// server to generate again, forever.
const linksGenerated =
  (store: CreateShareUrlStore, pageStore: ShareableLinksStore, current: ShareableLinksResource) =>
  (generated: LearningCollectionResource) => {
    store.ready(current);
    if (generated.payload.programFamilyShareUrls.length > 0) {
      pageStore.reload();
    }
  };

const handleGenerationError =
  (store: CreateShareUrlStore, current: ShareableLinksResource) => (error: ShareUrlError) => {
    switch (error.kind) {
      case 'missing-link-error':
        // No generate link: the collection already has its links.
        store.ready(current);
        break;
      default:
        store.error(error);
        warnAndNotify('CreateShareUrlReactions', error.kind, error);
    }
  };

const generateShareUrlsWhenNonePresent = (
  current: ShareableLinksResource,
  store: CreateShareUrlStore,
  pageStore: ShareableLinksStore,
) =>
  Task.succeed<ShareUrlError, ReadonlyArray<Link>>(current.links)
    .andThen(findLinkByT({ rel: 'generate' }))
    .andThen(callApi(learningCollectionResourceDecoder, createShareUrlEncoder(current.payload.id)))
    .fork(handleGenerationError(store, current), linksGenerated(store, pageStore, current));

class CreateShareUrlReactions extends ReactionComponent<CreateShareUrlStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store, pageStore, learningCollectionResource } = this.props;
    switch (state.kind) {
      case 'waiting':
        generateShareUrlsWhenNonePresent(learningCollectionResource, store, pageStore);
        break;
      case 'ready':
        break;
      case 'creating':
        toTask<ShareUrlError, Link>(
          missingLinkError({ rel: 'create' }),
          findLinkBy({ rel: 'create' }, learningCollectionResource.links),
        )
          .andThen(
            callApi(
              learningCollectionResourceDecoder,
              createShareUrlEncoder(learningCollectionResource.payload.id),
            ),
          )
          .fork(
            handleReportsError(store),
            linksAdded(store, pageStore, learningCollectionResource),
          );
        break;
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default CreateShareUrlReactions;
